import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  FileText,
  CheckCircle,
  AlertTriangle,
  Upload,
  Clock,
  CheckCheck,
  X,
  AtSign,
} from 'lucide-react'
import { useNotifications } from '@/features/notifications/hooks/use-notifications'
import { useAuth } from '@/contexts/AuthContext'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { Notification, NotificationType, NotificationRecipientRole } from '@/types'

function getRecipientRole(personaRole: string): NotificationRecipientRole {
  switch (personaRole) {
    case 'coordinator': return 'coordinator'
    case 'admin': return 'admin'
    case 'client': return 'client'
    default: return 'coordinator'
  }
}

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'script_sent':
      return { icon: FileText, color: 'text-amber-400' }
    case 'approval_needed':
      return { icon: Clock, color: 'text-accent-orange' }
    case 'approved':
      return { icon: CheckCircle, color: 'text-accent-green' }
    case 'adjustment_requested':
      return { icon: AlertTriangle, color: 'text-accent-orange' }
    case 'published':
      return { icon: Upload, color: 'text-primary-light' }
    case 'deadline_approaching':
      return { icon: Clock, color: 'text-accent-red' }
    case 'mentioned':
      return { icon: AtSign, color: 'text-primary-light' }
    default:
      return { icon: Bell, color: 'text-dim' }
  }
}

function NotificationItem({
  notification,
  onRead,
  onNavigate,
}: {
  notification: Notification
  onRead: (id: string) => void
  onNavigate: (id: string, commentId?: string) => void
}) {
  const { icon: Icon, color } = getNotificationIcon(notification.type)
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: ptBR,
  })

  const handleClick = () => {
    if (!notification.is_read) {
      onRead(notification.id)
    }
    if (notification.delivery_id) {
      // Carry comment anchor so DetalheEntrega can scroll + highlight it.
      const commentId = (notification as { metadata?: { comment_id?: string } }).metadata?.comment_id
      onNavigate(notification.delivery_id, commentId)
    }
  }

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors cursor-pointer ${
        notification.is_read
          ? 'opacity-70 hover:bg-card-hover'
          : 'bg-primary/5 border-l-2 border-primary hover:bg-primary/10'
      }`}
    >
      <div className={`mt-0.5 shrink-0 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[13px] font-medium leading-tight ${notification.is_read ? 'text-text' : 'text-bright'}`}>
          {notification.title}
        </p>
        {notification.message && (
          <p className="text-dim text-[11px] mt-0.5 truncate">{notification.message}</p>
        )}
        <p className="text-muted text-[10px] mt-1">{timeAgo}</p>
      </div>
      {!notification.is_read && (
        <div className="mt-1.5 shrink-0">
          <div className="w-2 h-2 rounded-full bg-primary" />
        </div>
      )}
    </button>
  )
}

export default function NotificationCenter() {
  const { persona, user } = useAuth()
  const recipientRole = getRecipientRole(persona.role)
  const { notifications, loading, markAsRead, markAllAsRead, unreadCount } = useNotifications(recipientRole, user?.id)
  const [isOpen, setIsOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNavigate = (deliveryId: string, commentId?: string) => {
    setIsOpen(false)
    const hash = commentId ? `#comment-${commentId}` : ''
    navigate(`/entrega/${deliveryId}${hash}`)
  }

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-xl transition-colors cursor-pointer ${
          unreadCount > 0
            ? 'text-bright hover:bg-card-hover'
            : 'text-dim hover:text-text hover:bg-card-hover'
        }`}
        title="Notificações"
      >
        <Bell className="w-[18px] h-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-accent-red text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="absolute left-full ml-3 bottom-0 w-80 bg-card border border-card-border rounded-2xl shadow-lg overflow-hidden z-[60]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-card-border">
            <h3 className="text-bright text-sm font-semibold font-heading">Notificações</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-1 text-[11px] text-dim hover:text-text transition-colors cursor-pointer"
                  title="Marcar todas como lidas"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Marcar lidas
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-dim hover:text-text transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div className="w-5 h-5 border-2 border-dim border-t-primary rounded-full animate-spin mx-auto" />
                <p className="text-dim text-xs mt-2">Carregando...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-muted mx-auto mb-2" />
                <p className="text-dim text-sm">Nenhuma notificação</p>
                <p className="text-muted text-xs mt-1">Você será notificado quando algo precisar da sua atenção</p>
              </div>
            ) : (
              <div className="divide-y divide-card-border">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={markAsRead}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
